import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageBackground, Platform, StyleSheet, Text } from 'react-native';
import { useSelector } from 'react-redux';

import { AvatarLevelPosition, AvatarSparklePosition } from '../../../../typings/types';
import {
	AvatarFramePng,
	AvatarFrameSparklePng,
	AvatarFrameVipPng,
	ProfileImage,
	ProfileLevelFramePng,
	UsernameFrameDownLightPng,
	UsernameFramePng,
	VipBadgeFramePng,
} from '../../../assets';
import { RootState } from '../../../store';
import { getAvatarUrl } from '../../../utils/axiosInstance';
import { useDevice } from '../../../utils/hooks/useDevice';

const AvatarFrame = ({
	hasGradient,
	levelPosition,
	sparklePosition,
	showVipBadge,
	showUsernameFrame,
	sizeMultiplier = 1,
}: {
	hasGradient?: boolean;
	levelPosition?: AvatarLevelPosition;
	sparklePosition?: AvatarSparklePosition;
	showVipBadge?: boolean;
	showUsernameFrame?: boolean;
	sizeMultiplier?: number;
}) => {
	const { t } = useTranslation();
	const player = useSelector((s: RootState) => s.auth.user);
	const { isAndroid, isDesktop, isIOS } = useDevice();
	const levelBadgeSize = 29 * sizeMultiplier;
	const levelTextStyle = isIOS ? styles.levelTextIos : styles.levelText;
	const levelTextOffset = isIOS ? 0 : isAndroid ? 0 : isDesktop ? 0 : 0;

	return (
		<ImageBackground
			source={player?.isVip ? AvatarFrameVipPng : AvatarFramePng}
			style={{
				top: hasGradient ? 20 * sizeMultiplier : 0,
				left: hasGradient ? 20 * sizeMultiplier : 0,
				width: 63 * sizeMultiplier,
				height: 63 * sizeMultiplier,
			}}
		>
			{/*SPARKLE*/}
			{!!sparklePosition && (
				<Image
					source={AvatarFrameSparklePng}
					style={
						sparklePosition === AvatarSparklePosition.Left
							? {
									position: 'absolute',
									zIndex: 1,
									width: 35 * sizeMultiplier,
									height: 35 * sizeMultiplier,
									top: -8 * sizeMultiplier,
									left: -8 * sizeMultiplier,
								}
							: {
									// todo
									display: 'none',
								}
					}
				/>
			)}

			{/*LEVEL*/}
			{!!levelPosition && (
				<ImageBackground
					source={ProfileLevelFramePng}
					style={[
						styles.levelBadge,
						{
							width: levelBadgeSize,
							height: levelBadgeSize,
							top: -1 * sizeMultiplier,
							right: -7 * sizeMultiplier,
						},
						levelPosition === AvatarLevelPosition.TopRight
							? isIOS && styles.levelBadgeIos
							: styles.hidden,
					]}
				>
					<Text
						numberOfLines={1}
						ellipsizeMode='tail'
						style={[
							levelTextStyle,
							isIOS && { lineHeight: levelBadgeSize },
							{ top: levelTextOffset },
						]}
					>
						{player?.level ? player.level : 1}
					</Text>
				</ImageBackground>
			)}

			{(() => {
				const avatarUri =
					player?.avatarFileId && typeof player.avatarFileId === 'string'
						? getAvatarUrl(player.avatarFileId)
						: undefined;
				const avatarSource = avatarUri ? { uri: avatarUri } : ProfileImage;

				return (
					<Image
						source={avatarSource}
						style={{
							position: 'absolute',
							width: 55 * sizeMultiplier,
							height: 55 * sizeMultiplier,
							top: 4 * sizeMultiplier,
							left: 4 * sizeMultiplier,
							borderRadius: (55 * sizeMultiplier) / 2,
						}}
						resizeMode='cover'
					/>
				);
			})()}

			{/*VIP BADGE*/}
			{showVipBadge && <Image source={VipBadgeFramePng} style={styles.vipBadge} />}

			{/*USERNAME FRAME*/}
			{showUsernameFrame && (
				<ImageBackground source={UsernameFramePng} style={styles.usernameFrame}>
					<Text numberOfLines={1} ellipsizeMode='tail' style={styles.usernameText}>
						{player?.username
							? player.username
							: t('T_SITE_DEFAULT_USER', { defaultValue: 'User' })}
					</Text>
					<Image source={UsernameFrameDownLightPng} style={styles.usernameFrameLight} />
				</ImageBackground>
			)}
		</ImageBackground>
	);
};

const styles = StyleSheet.create({
	levelBadge: {
		position: 'absolute',
		zIndex: 1,
	},
	levelBadgeIos: {
		justifyContent: 'center',
		alignItems: 'center',
	},
	levelText: {
		width: '100%',
		height: '100%',
		textAlign: 'center',
		verticalAlign: 'middle',
		color: '#fff',
		fontSize: 14,
		fontWeight: 'bold',
		includeFontPadding: false,
		paddingTop: Platform.OS !== 'web' ? -2 : 4,
	},
	levelTextIos: {
		width: '100%',
		height: '100%',
		textAlign: 'center',
		verticalAlign: 'middle',
		color: '#fff',
		fontSize: 14,
		fontWeight: 'bold',
	},
	hidden: {
		display: 'none',
	},
	vipBadge: {
		position: 'absolute',
		top: 28,
		left: 42,
		width: 27,
		height: 27,
	},
	usernameFrame: {
		position: 'absolute',
		justifyContent: 'center',
		top: 80,
		left: 4,
		width: 91,
		height: 26,
	},
	usernameText: {
		textAlign: 'center',
		color: 'white',
		fontSize: 12,
		fontWeight: 'bold',
		paddingBottom: 4,
	},
	usernameFrameLight: {
		position: 'absolute',
		top: 13,
		left: -43,
		width: 180,
		height: 25,
	},
});

export default AvatarFrame;
